// hooks/useBridge.js
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bridgeService } from "../services/bridge.service";

const BRIDGE_STATUS_KEY = ["bridge", "status"];

export function useBridgeStatus() {
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: BRIDGE_STATUS_KEY,
    queryFn: () => bridgeService.getStatus(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  return {
    bridge: data ?? null,
    isLoadingStatus: isLoading,
    isRefetchingStatus: isRefetching,
    statusError: error,
    refetchStatus: refetch,
  };
}

/**
 * Starts (or resumes) Bridge onboarding. On success, seeds the status query
 * cache directly with the response so the UI doesn't need a second round
 * trip just to show the links it was just handed back.
 */
export function useStartBridge() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: () => bridgeService.start(),
    onSuccess: (data) => {
      queryClient.setQueryData(BRIDGE_STATUS_KEY, data);
    },
  });

  return {
    startBridge: mutateAsync,
    isStarting: isPending,
    startError: error,
  };
}

/**
 * Sandbox-only. Simulates Bridge completing hosted KYC/TOS review, then
 * provisions the virtual account like a real approval would. The backend
 * rejects this outside sandbox (ENVIRONMENT=production/prod), and callers
 * should also gate any UI control behind a dev-only flag — a real user
 * should never be able to reach this.
 */
export function useSimulateBridgeApproval() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: () => bridgeService.simulateApproval(),
    onSuccess: (data) => {
      queryClient.setQueryData(BRIDGE_STATUS_KEY, data);
    },
  });

  return {
    simulateApproval: mutateAsync,
    isSimulating: isPending,
    simulateError: error,
  };
}